import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TriangleAlert } from 'lucide-react';
import { Modal } from '~/components/overlays';
import {
  useDeclinePriceNegotiationRequest,
  useRespondToPriceNegotiationInApp,
  useSellerPriceNegotiationDetail,
} from '~/api';
import {
  PriceNegotiationResponseForm,
  type PriceNegotiationData,
  type PriceNegotiationResponseValues,
} from '~/pages/respond-to-price-negotiation/PriceNegotiationResponseForm';
import type { SellerPriceNegotiationDetailQuery } from '~/graphql/graphql';
import {
  priceNegotiationProduct,
  type PriceNegotiationRow,
} from '~/pages/price-negotiations/priceNegotiationProduct';

interface RespondToNegotiationDialogProps {
  negotiation: PriceNegotiationRow;
  onClose: () => void;
  onResponded: (negotiationId: string) => void;
}

type NegotiationDetail = Partial<
  Pick<
    SellerPriceNegotiationDetailQuery['sellerPriceNegotiation'],
    'tallyRecipe' | 'currentSellerListPrice' | 'offerCurrency'
  >
>;

function buildResponseData(
  negotiation: PriceNegotiationRow,
  detail: NegotiationDetail,
  tallyProductLabel: string,
  lengthProductLabel: string
): PriceNegotiationData {
  const isTally = negotiation.platformProductCannedTally != null;
  const product = priceNegotiationProduct(negotiation);
  const common = {
    id: negotiation.id,
    productName:
      product.name ?? (isTally ? tallyProductLabel : lengthProductLabel),
    productLength: product.lengthLabel ?? '',
    currentSellerPrice: detail.currentSellerListPrice ?? null,
    targetPrice: negotiation.targetPrice ?? null,
    unit: negotiation.unit,
    offerCurrency: detail.offerCurrency,
    buyerNote: negotiation.buyerNote ?? null,
    buyerFullName: negotiation.createdBy
      ? `${negotiation.createdBy.firstName} ${negotiation.createdBy.lastName}`.trim()
      : '',
    buyerOrganization: negotiation.buyingOrganization?.name ?? '',
    // eslint-disable-next-line no-restricted-syntax -- the seller sees their own organization, which has no seller code
    sellerOrganization: negotiation.sellingOrganization?.name ?? '',
  };

  if (isTally) {
    return {
      ...common,
      isTally: true,
      tallyRecipe: detail.tallyRecipe ?? [],
    };
  }

  return { ...common, isTally: false };
}

export function RespondToNegotiationDialog({
  negotiation,
  onClose,
  onResponded,
}: Readonly<RespondToNegotiationDialogProps>) {
  const { t } = useTranslation('price-negotiations');
  const [failureMessage, setFailureMessage] = useState<string | null>(null);
  const { data: detailData, isPending: isLoadingDetail } =
    useSellerPriceNegotiationDetail(negotiation.id);
  const { mutate: respond, isPending: isResponding } =
    useRespondToPriceNegotiationInApp();
  const { mutate: decline, isPending: isDeclining } =
    useDeclinePriceNegotiationRequest();

  const data = buildResponseData(
    negotiation,
    detailData?.sellerPriceNegotiation ?? {},
    t('price_negotiations.tally_product'),
    t('price_negotiations.length_product')
  );

  const finish = () => {
    onResponded(negotiation.id);
    onClose();
  };

  const showFailure = (error: Error) =>
    setFailureMessage(error.message || t('price_negotiations.respond_failed'));

  const handleSubmit = (values: PriceNegotiationResponseValues) => {
    setFailureMessage(null);
    respond(
      {
        id: negotiation.id,
        input: {
          offeredPrice: values.offeredPrice,
          sellerNote: values.sellerNote,
        },
      },
      { onSuccess: finish, onError: showFailure }
    );
  };

  const handleDecline = (reason: string | null) => {
    setFailureMessage(null);
    decline(
      { id: negotiation.id, reason: reason ?? undefined },
      { onSuccess: finish, onError: showFailure }
    );
  };

  return (
    <Modal isOpen onClose={onClose} className="w-full max-w-2xl">
      {failureMessage && (
        <div
          role="alert"
          data-testid="price-negotiation-respond-error"
          className="mx-6 mt-6 flex items-start gap-2 rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700"
        >
          <TriangleAlert size={16} className="mt-0.5 shrink-0" aria-hidden />
          <span>{failureMessage}</span>
        </div>
      )}
      <PriceNegotiationResponseForm
        data={data}
        loading={isLoadingDetail}
        submitting={isResponding || isDeclining}
        onSubmit={handleSubmit}
        onDecline={handleDecline}
        onCancel={onClose}
      />
    </Modal>
  );
}
